import { useState, type FormEvent } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { createSecretChangeRequest, fetchSecretValues, patchSecret } from '@/apps/apis/secrets';
import { SECRETS_QUERY_KEYS, useSecretAudit, useSecretHistory, useSecretsList } from '@/apps/apis/secrets/hooks';
import type { SecretChangeOp, SecretPatchRequest, SecretValues, SecretsMe } from '@/apps/apis/secrets/types';
import { ROUTES } from '@/routes/routes';
import {
  asArray,
  errorStatus,
  fieldProblem,
  formatDate,
  readableError,
  SecretRequestsTable,
  SecretsGate,
} from './common';

interface DraftField {
  key: string;
  value: string;
  restricted: boolean;
  deleted: boolean;
  isNew: boolean;
}

/** Turns the edited rows into one PATCH body, or a validation message. */
const buildPatch = (loaded: SecretValues, draft: DraftField[]): SecretPatchRequest | string => {
  const original = new Map(asArray(loaded.fields).map((field) => [field.key, field]));
  const body: Required<Omit<SecretPatchRequest, 'baseVersionId'>> = {
    set: {},
    unset: [],
    restrict: [],
    unrestrict: [],
  };
  const seen = new Set<string>();
  for (const row of draft) {
    const key = row.key.trim();
    if (row.deleted) {
      if (!row.isNew) {
        body.unset.push(key);
      }
      continue;
    }
    const problem = fieldProblem(key, row.value);
    if (problem) {
      return problem;
    }
    if (seen.has(key) || (row.isNew && original.has(key))) {
      return `${key}: duplicate key.`;
    }
    seen.add(key);
    const before = original.get(key);
    if (row.isNew || (before?.value ?? '') !== row.value) {
      body.set[key] = row.value;
    }
    if (row.restricted !== (before?.restricted ?? false)) {
      (row.restricted ? body.restrict : body.unrestrict).push(key);
    }
  }
  const request: SecretPatchRequest = { baseVersionId: loaded.versionId };
  if (Object.keys(body.set).length) {
    request.set = body.set;
  }
  if (body.unset.length) {
    request.unset = body.unset;
  }
  if (body.restrict.length) {
    request.restrict = body.restrict;
  }
  if (body.unrestrict.length) {
    request.unrestrict = body.unrestrict;
  }
  return Object.keys(request).length === 1 ? 'Nothing to save.' : request;
};

const ChangeRequestForm = ({ path, onSubmitted }: { path: string; onSubmitted: () => void }) => {
  const [op, setOp] = useState<SecretChangeOp>('UPDATE');
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState<{ variant: 'success' | 'danger'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const trimmedKey = key.trim();
    const problem = fieldProblem(trimmedKey, op === 'DELETE' ? undefined : value);
    if (problem || !reason.trim()) {
      setMessage({ variant: 'danger', text: problem ?? 'A reason is required.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const created = await createSecretChangeRequest(path, {
        op,
        key: trimmedKey,
        ...(op === 'DELETE' ? {} : { value }),
        reason: reason.trim(),
      });
      setKey('');
      setValue('');
      setReason('');
      setMessage({ variant: 'success', text: `Request ${created?.requestId ?? ''} submitted for review.` });
      onSubmitted();
    } catch (caught) {
      setMessage({ variant: 'danger', text: readableError(caught, 'Unable to submit the request.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={(event) => void submit(event)}>
      {message ? <Alert variant={message.variant}>{message.text}</Alert> : null}
      <div className="d-flex gap-2 flex-wrap align-items-start">
        <Form.Select
          size="sm"
          aria-label="Operation"
          style={{ width: 120 }}
          value={op}
          onChange={(event) => setOp(event.target.value as SecretChangeOp)}
        >
          <option value="ADD">ADD</option>
          <option value="UPDATE">UPDATE</option>
          <option value="DELETE">DELETE</option>
        </Form.Select>
        <Form.Control
          size="sm"
          aria-label="Key"
          placeholder="KEY_NAME"
          style={{ width: 220 }}
          value={key}
          onChange={(event) => setKey(event.target.value)}
        />
        {op === 'DELETE' ? null : (
          <Form.Control
            size="sm"
            aria-label="Value"
            placeholder="value"
            style={{ width: 260 }}
            autoComplete="off"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        )}
        <Form.Control
          size="sm"
          aria-label="Reason"
          placeholder="reason"
          style={{ minWidth: 260, flex: 1 }}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Button size="sm" type="submit" disabled={saving}>
          Request change
        </Button>
      </div>
    </Form>
  );
};

const SecretDetail = ({ path, me }: { path: string; me: SecretsMe }) => {
  const queryClient = useQueryClient();
  const secrets = useSecretsList();
  const history = useSecretHistory(path);
  const audit = useSecretAudit(path, me.canManage);
  // Revealed values live only in this component's state: never in a query cache, gone on unmount.
  const [values, setValues] = useState<SecretValues | null>(null);
  const [draft, setDraft] = useState<DraftField[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const summary = asArray(secrets.data).find((secret) => secret?.path === path);
  const refreshLists = () => void queryClient.invalidateQueries({ queryKey: SECRETS_QUERY_KEYS.all });

  const reveal = async () => {
    setBusy(true);
    setError(null);
    setDraft(null);
    try {
      const result = await fetchSecretValues(path);
      setValues({ versionId: result?.versionId ?? '', fields: asArray(result?.fields) });
    } catch (caught) {
      setError(readableError(caught, 'Unable to load values.'));
    } finally {
      setBusy(false);
    }
  };

  const hide = () => {
    setValues(null);
    setDraft(null);
  };

  const startEdit = () =>
    setDraft(
      asArray(values?.fields).map((field) => ({
        key: field.key,
        value: field.value ?? '',
        restricted: field.restricted === true,
        deleted: false,
        isNew: false,
      })),
    );

  const updateRow = (index: number, patch: Partial<DraftField>) =>
    setDraft((rows) => rows?.map((row, at) => (at === index ? { ...row, ...patch } : row)) ?? rows);

  const save = async () => {
    if (!values || !draft) {
      return;
    }
    const body = buildPatch(values, draft);
    if (typeof body === 'string') {
      setError(body);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await patchSecret(path, body);
      // The server applied exactly this diff on top of baseVersionId, so the draft is the new state.
      setValues({
        versionId: result?.versionId ?? '',
        fields: draft
          .filter((row) => !row.deleted)
          .map((row) => ({ key: row.key.trim(), value: row.value, restricted: row.restricted })),
      });
      setDraft(null);
      refreshLists();
    } catch (caught) {
      setError(
        errorStatus(caught) === 409
          ? 'This secret changed since you loaded it. Reload the values and apply your edit again.'
          : readableError(caught, 'Unable to save.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const hiddenKeys = asArray(summary?.fieldKeys);

  return (
    <Container fluid className="py-4">
      <div className="mb-3">
        <Link to={ROUTES.secrets.list}>Secrets</Link> / <strong>{path}</strong>
        <h2 className="mb-1">{summary?.name ?? path}</h2>
        <Alert variant="info" className="py-2 mb-0">
          Changing a value does not restart consuming services.
        </Alert>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
          <span>Fields</span>
          <div className="d-flex gap-2">
            {values ? (
              <>
                {me.canManage && !draft ? (
                  <Button size="sm" variant="outline-primary" onClick={startEdit} disabled={busy}>
                    Edit
                  </Button>
                ) : null}
                <Button size="sm" variant="outline-secondary" onClick={() => void reveal()} disabled={busy}>
                  Reload values
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={hide} disabled={busy}>
                  Hide
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline-primary" onClick={() => void reveal()} disabled={busy}>
                Reveal
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {busy && !values ? <Spinner animation="border" size="sm" /> : null}
          {draft ? (
            <>
              <Table size="sm" responsive className="align-middle">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Restricted</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {draft.map((row, index) => (
                    <tr key={index} className={row.deleted ? 'text-decoration-line-through text-muted' : undefined}>
                      <td>
                        {row.isNew ? (
                          <Form.Control
                            size="sm"
                            aria-label="New key"
                            placeholder="KEY_NAME"
                            value={row.key}
                            onChange={(event) => updateRow(index, { key: event.target.value })}
                          />
                        ) : (
                          <code>{row.key}</code>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          size="sm"
                          aria-label={`Value for ${row.key || 'new key'}`}
                          autoComplete="off"
                          disabled={row.deleted}
                          value={row.value}
                          onChange={(event) => updateRow(index, { value: event.target.value })}
                        />
                      </td>
                      <td>
                        <Form.Check
                          aria-label={`Restrict ${row.key || 'new key'}`}
                          disabled={row.deleted}
                          checked={row.restricted}
                          onChange={(event) => updateRow(index, { restricted: event.target.checked })}
                        />
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="link"
                          className="p-0"
                          onClick={() => updateRow(index, { deleted: !row.deleted })}
                        >
                          {row.deleted ? 'Undo' : 'Delete'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    setDraft((rows) => [
                      ...(rows ?? []),
                      { key: '', value: '', restricted: false, deleted: false, isNew: true },
                    ])
                  }
                >
                  Add field
                </Button>
                <Button size="sm" onClick={() => void save()} disabled={busy}>
                  Save
                </Button>
                <Button size="sm" variant="link" onClick={() => setDraft(null)} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </>
          ) : values ? (
            asArray(values.fields).length === 0 ? (
              <p className="text-muted mb-0">No fields.</p>
            ) : (
              <Table size="sm" responsive className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {asArray(values.fields).map((field) => (
                    <tr key={field?.key}>
                      <td>
                        <code>{field?.key}</code>{' '}
                        {field?.restricted ? (
                          <Badge bg="warning" text="dark">
                            restricted
                          </Badge>
                        ) : null}
                      </td>
                      <td>
                        {field?.value == null ? (
                          <span className="text-muted">(restricted)</span>
                        ) : (
                          <code className="text-break">{field.value}</code>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )
          ) : hiddenKeys.length > 0 ? (
            <Table size="sm" responsive className="align-middle mb-0">
              <tbody>
                {hiddenKeys.map((key) => (
                  <tr key={key}>
                    <td>
                      <code>{key}</code>
                    </td>
                    <td className="text-muted">hidden</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : busy ? null : (
            <p className="text-muted mb-0">Values are hidden. Reveal to view them; every reveal is audited.</p>
          )}
        </Card.Body>
      </Card>

      {me.canManage ? null : (
        <Card className="mb-3">
          <Card.Header>Request change</Card.Header>
          <Card.Body>
            <ChangeRequestForm path={path} onSubmitted={refreshLists} />
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header>History</Card.Header>
        <Card.Body>
          {history.error ? (
            <Alert variant="danger">{readableError(history.error, 'Unable to load history.')}</Alert>
          ) : history.isLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <SecretRequestsTable items={asArray(history.data)} me={me} />
          )}
        </Card.Body>
      </Card>

      {me.canManage ? (
        <Card className="mb-3">
          <Card.Header>Audit</Card.Header>
          <Card.Body>
            {audit.error ? (
              <Alert variant="danger">{readableError(audit.error, 'Unable to load audit events.')}</Alert>
            ) : audit.isLoading ? (
              <Spinner animation="border" size="sm" />
            ) : asArray(audit.data).length === 0 ? (
              <p className="text-muted mb-0">No audit events.</p>
            ) : (
              <Table size="sm" responsive className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Fields</th>
                    <th>Request</th>
                  </tr>
                </thead>
                <tbody>
                  {asArray(audit.data).map((event) => (
                    <tr key={event?.eventId}>
                      <td className="text-nowrap">{formatDate(event?.createdAt)}</td>
                      <td>{event?.action ?? '—'}</td>
                      <td>{event?.actorEmail ?? '—'}</td>
                      <td>{asArray(event?.fieldKeys).join(', ') || '—'}</td>
                      <td>{event?.requestId ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      ) : null}
    </Container>
  );
};

const SecretDetailPage = () => {
  const { '*': path = '' } = useParams();
  // Keyed by path so revealed values never carry over to another secret.
  return <SecretsGate>{(me) => <SecretDetail key={path} path={path} me={me} />}</SecretsGate>;
};

export default SecretDetailPage;
